import { ReactNode } from 'react';
import { CalendarRange } from 'lucide-react';
import { BsLinkedin } from 'react-icons/bs';
import { FaSquareXTwitter } from 'react-icons/fa6';
import {
    TbAB2,
    TbAnalyze,
    TbArrowsSplit,
    TbBinaryTree2,
    TbBolt,
    TbBrackets,
    TbBuildingBank,
    TbBuildingCommunity,
    TbBuildingEstate,
    TbBusinessplan,
    TbCalendarBolt,
    TbCalendarEvent,
    TbCalendarMonth,
    TbCalendarStar,
    TbCalendarStats,
    TbCalendarUp,
    TbCashBanknote,
    TbChartBar,
    TbChartPie,
    TbCheck,
    TbChecklist,
    TbCube3dSphere,
    TbDots,
    TbFileDescription,
    TbGps,
    TbHash,
    TbIdBadge,
    TbLink,
    TbMail,
    TbMailBolt,
    TbMailUp,
    TbMapPin,
    TbMathFunction,
    TbNumber123,
    TbPaperclip,
    TbPercentage,
    TbPhone,
    TbProgressCheck,
    TbSelect,
    TbSourceCode,
    TbSparkles,
    TbTags,
    TbTextScan2,
    TbTextWrapColumn,
    TbTransactionDollar,
    TbTrendingUp,
    TbUserScan,
    TbUserStar,
    TbWorld,
    TbZoomScan,
} from 'react-icons/tb';

import {
    AirtableSVGIcon,
    AsanaSVGIcon,
    AttioSVGIcon,
    BoxSVGIcon,
    ClickUpSVGICON,
    CloseCRMSVGIcon,
    CodaSVGIcon,
    ConfluenceSVGIcon,
    DocuSignSVGIcon,
    DropboxSVGIcon,
    GitHubSVGIcon,
    GmailSVGIcon,
    GoogleCalendarSVGIcon,
    GoogleDocsSVGIcon,
    GoogleDriveSVGIcon,
    GoogleSheetsSVGIcon,
    GraphySVGIcon,
    HexSVGIcon,
    HubSpotSVGIcon,
    LookerSVGIcon,
    LoomSVGIcon,
    LucidSVGIcon,
    MicrosoftOneDriveSVGIcon,
    MicrosoftOutlookSVGIcon,
    MicrosoftPowerBISVGIcon,
    MicrosoftSharePointSVGIcon,
    MiroSVGIcon,
    MondayDotComSVGIcon,
    NotionSVGIcon,
    PipedriveSVGIcon,
    SalesforceSVGIcon,
    SlackSVGIcon,
    SmartsheetSVGIcon,
    TableauSVGIcon,
    VimeoSVGIcon,
    WealthboxSVGIcon,
    WrikeSVGIcon,
    YouTubeSVGIcon,
    ZohoAnalyticsSVGIcon,
} from '@/assets';
import {
    ConnectedAccountType,
    ListViewColumnDataType,
    ReportComponentType,
} from '@/types';

import type { MapToken } from './types';

export const createIntegrationMaps = (token: MapToken) => {
    const integrations = [
        {
            id: 'google-sheets', //!TODO: Change to 'google-sheets' once we push API to prod with newly updated logic
            label: 'Google Sheets',
            tag: 'Productivity',
            icon: GoogleSheetsSVGIcon,
            active: true,
        },
        {
            id: 'google-drive',
            label: 'Google Drive',
            tag: 'Storage',
            icon: GoogleDriveSVGIcon,
            active: true,
        },
        {
            id: 'one-drive',
            label: 'Microsoft OneDrive',
            tag: 'Storage',
            icon: MicrosoftOneDriveSVGIcon,
            active: false,
        },
        {
            id: 'dropbox',
            label: 'Dropbox',
            tag: 'Storage',
            icon: DropboxSVGIcon,
            active: true,
        },
        {
            id: 'notion',
            label: 'Notion',
            tag: 'Productivity',
            icon: NotionSVGIcon,
            active: true,
        },
        {
            id: 'microsoft-sharepoint',
            label: 'Microsoft SharePoint',
            tag: 'Productivity',
            icon: MicrosoftSharePointSVGIcon,
            active: false,
        },
        {
            id: 'box',
            label: 'Box',
            tag: 'Storage',
            icon: BoxSVGIcon,
            active: true,
        },
    ] as any;
    
    const activeIntegrations = integrations?.filter((gi: any) => gi?.active === true);
    
    const globalCloudStorageProviders = activeIntegrations?.filter((gi: any) => gi?.tag === 'Storage');
    
    const integrationLabelToIconMap = {
        [ConnectedAccountType.GMAIL]: GmailSVGIcon,
        [ConnectedAccountType.GOOGLE_SHEETS]: GoogleSheetsSVGIcon,
        [ConnectedAccountType.GOOGLE_DRIVE]: GoogleDriveSVGIcon,
        [ConnectedAccountType.MICROSOFT_ONEDRIVE]: MicrosoftOneDriveSVGIcon,
        [ConnectedAccountType.BOX]: BoxSVGIcon,
        [ConnectedAccountType.DROPBOX]: DropboxSVGIcon,
        [ConnectedAccountType.NOTION]: NotionSVGIcon,
        [ConnectedAccountType.CLICKUP]: ClickUpSVGICON,
        [ConnectedAccountType.CLOSE]: CloseCRMSVGIcon,
        [ConnectedAccountType.CODA]: CodaSVGIcon,
        [ConnectedAccountType.DOCUSIGN]: DocuSignSVGIcon,
        [ConnectedAccountType.ATTIO]: AttioSVGIcon,
        [ConnectedAccountType.WEALTHBOX]: WealthboxSVGIcon,
        [ConnectedAccountType.AIRTABLE]: AirtableSVGIcon,
        [ConnectedAccountType.CONFLUENCE]: ConfluenceSVGIcon,
        [ConnectedAccountType.MONDAY]: MondayDotComSVGIcon,
        [ConnectedAccountType.PIPEDRIVE]: PipedriveSVGIcon,
        [ConnectedAccountType.MIRO]: MiroSVGIcon,
        [ConnectedAccountType.SALESFORCE]: SalesforceSVGIcon,
        [ConnectedAccountType.HUBSPOT]: HubSpotSVGIcon,
        [ConnectedAccountType.SLACK]: SlackSVGIcon,
        [ConnectedAccountType.ASANA]: AsanaSVGIcon,
        [ConnectedAccountType.WRIKE]: WrikeSVGIcon,
        [ConnectedAccountType.SMARTSHEET]: SmartsheetSVGIcon,
    } as Record<ConnectedAccountType, string>;
    
    const embedComponentTypeToIconMap = {
        [ReportComponentType.YOUTUBE]: YouTubeSVGIcon,
        [ReportComponentType.LOOM]: LoomSVGIcon,
        [ReportComponentType.VIMEO]: VimeoSVGIcon,
        [ReportComponentType.GOOGLE_CALENDAR]: GoogleCalendarSVGIcon,
        [ReportComponentType.MICROSOFT_OUTLOOK_CALENDAR]: MicrosoftOutlookSVGIcon,
        [ReportComponentType.GOOGLE_DRIVE]: GoogleDriveSVGIcon,
        [ReportComponentType.GOOGLE_DOCS]: GoogleDocsSVGIcon,
        [ReportComponentType.GOOGLE_SHEETS]: GoogleSheetsSVGIcon,
        [ReportComponentType.MICROSOFT_ONEDRIVE]: MicrosoftOneDriveSVGIcon,
        [ReportComponentType.DROPBOX]: DropboxSVGIcon,
        [ReportComponentType.BOX]: BoxSVGIcon,
        [ReportComponentType.NOTION]: NotionSVGIcon,
        [ReportComponentType.GITHUB]: GitHubSVGIcon,
        [ReportComponentType.HEX]: HexSVGIcon,
        [ReportComponentType.CONFLUENCE]: ConfluenceSVGIcon,
        [ReportComponentType.MIRO]: MiroSVGIcon,
        [ReportComponentType.GRAPHY]: GraphySVGIcon,
        [ReportComponentType.LUCID]: LucidSVGIcon,
        [ReportComponentType.MICROSOFT_POWER_BI]: MicrosoftPowerBISVGIcon,
        [ReportComponentType.LOOKER]: LookerSVGIcon,
        [ReportComponentType.TABLEAU]: TableauSVGIcon,
        [ReportComponentType.ZOHO_ANALYTICS]: ZohoAnalyticsSVGIcon,
    } as Record<ReportComponentType, string>;
    
    const listColumnDataTypeToIconMap = {
        [ListViewColumnDataType.SHORT_TEXT]: <TbTextScan2 />,
        [ListViewColumnDataType.LONG_TEXT]: <TbTextWrapColumn />,
        [ListViewColumnDataType.NUMBER]: <TbHash />,
        [ListViewColumnDataType.DATE]: <TbCalendarEvent />,
        [ListViewColumnDataType.BOOLEAN]: <TbCheck />,
        [ListViewColumnDataType.SELECT]: <TbSelect />,
        [ListViewColumnDataType.MULTISELECT]: <TbChecklist />,
        [ListViewColumnDataType.CURRENCY]: <TbTransactionDollar />,
        [ListViewColumnDataType.PERCENTAGE]: <TbPercentage />,
        [ListViewColumnDataType.MONTH]: <TbCalendarMonth />,
        [ListViewColumnDataType.QUARTER]: <TbChartPie />,
        [ListViewColumnDataType.YEAR]: <TbCalendarStats />,
        [ListViewColumnDataType.DATE_RANGE]: <CalendarRange />,
        [ListViewColumnDataType.URL]: <TbLink />,
        [ListViewColumnDataType.EMAIL]: <TbMail />,
        [ListViewColumnDataType.PHONE]: <TbPhone />,
        [ListViewColumnDataType.ADDRESS]: <TbMapPin />,
        [ListViewColumnDataType.JSON]: <TbBrackets />,
        [ListViewColumnDataType.ATTACHMENT]: <TbPaperclip />,
        [ListViewColumnDataType.RELATION]: <TbAB2 />,
        [ListViewColumnDataType.MULTIRELATION]: <TbArrowsSplit />,
        [ListViewColumnDataType.FORMULA]: <TbMathFunction />,
        [ListViewColumnDataType.LOOKUP]: <TbZoomScan />,
        [ListViewColumnDataType.AGGREGATION]: <TbChartBar />,
        [ListViewColumnDataType.STATUS]: <TbAnalyze />,
        [ListViewColumnDataType.PROGRESS]: <TbProgressCheck />,
        [ListViewColumnDataType.AI_SUMMARY]: <TbSparkles />,
        [ListViewColumnDataType.AI_CLASSIFICATION]: <TbBinaryTree2 />,
        [ListViewColumnDataType.EMBED]: <TbSourceCode style={{ transform: 'rotate(180deg)' }} />,
        [ListViewColumnDataType.OBJECT]: <TbCube3dSphere />,
    } as Record<ListViewColumnDataType, ReactNode>;
    
    const listColumnKeyToIconMap = {
        person: <TbUserScan />,
        title: <TbIdBadge />,
        company: <TbBuildingEstate />,
        description: <TbFileDescription />,
        status: <TbAnalyze />,
        connectionStrength: <TbBolt />,
        emailAddress: <TbMail />,
        phoneNumber: <TbPhone />,
        location: <TbMapPin />,
        linkedin: <BsLinkedin />,
        twitter: <FaSquareXTwitter />,
        lastEmailInteraction: <TbMailBolt />,
        lastCalendarInteraction: <TbCalendarBolt />,
        nextEmailInteraction: <TbMailUp />,
        nextCalendarInteraction: <TbCalendarUp />,
        categories: <TbTags />,
        website: <TbWorld />,
        keyContact: <TbUserStar />,
        address: <TbBuildingCommunity />,
        headquarters: <TbBuildingCommunity />,
        city: <TbBuildingBank />,
        stateProvinence: <TbGps />,
        zipCode: <TbNumber123 />,
        capitalRaised: <TbBusinessplan />,
        annualRevenue: <TbCashBanknote />,
        foundingYear: <TbCalendarStar />,
        tickerSymbol: <TbTrendingUp />,
        enriched: <TbSparkles style={{ color: token.purple4 }} />,
        more: <TbDots />,
    } as Record<string, ReactNode>;
    
    const intergationMaps = {
        integrationLabelToIconMap,
        activeIntegrations,
        globalCloudStorageProviders,
        listColumnDataTypeToIconMap,
        listColumnKeyToIconMap,
        embedComponentTypeToIconMap,
    };

    return intergationMaps;
};
